import { useQuery } from '@tanstack/react-query';
import { fooddb, FOODDB_TO_OUTLET, type AggregatorProvider } from '@/lib/supabase/fooddb';
import { slotsFor, type MetricConcept } from '@/lib/aggregator-metrics';
import {
  getCompareRange,
  getPeriodRange,
  toISTDateString,
  type DateRange,
  type PeriodKey,
} from '@/lib/periods';

interface Args {
  period: PeriodKey;
}

export interface ProviderSnapshot {
  netSales: number;
  deliveredOrders: number;
  ordersPlaced: number;
  impressions: number;
  poorRated: number;
  totalRated: number;
  /** Sales / Delivered orders. */
  aov: number;
  /** Orders placed / Impressions (%). */
  funnelConvPct: number;
  /** Poor rated / Total rated (%). 0 if no ratings. */
  poorRatedPct: number;
}

export interface OutletBreakdown {
  outletId: string;
  swiggy: ProviderSnapshot;
  zomato: ProviderSnapshot;
  prevSwiggy: ProviderSnapshot;
  prevZomato: ProviderSnapshot;
  /** Share of total net sales that came from Swiggy (0-100). */
  swiggySharePct: number;
}

interface Result {
  outlets: OutletBreakdown[];
  current: DateRange;
  previous: DateRange;
}

const CONCEPTS: MetricConcept[] = [
  'sales.net_sales',
  'sales.delivered_orders',
  'funnel.orders_placed',
  'funnel.impressions',
  'ratings.poor_rated',
  'ratings.total_rated',
];

interface Row {
  outlet_id: string;
  provider: AggregatorProvider;
  category: string;
  metric: string;
  value: number | string;
}

async function fetchAll(range: DateRange): Promise<Row[]> {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));

  // Build OR filters: for each provider, slots(concept) => (category, metric)
  // We can't easily do `.or` across two columns in PostgREST without a view,
  // so we just fire one query per (provider, slot) and merge. Small fanout
  // (~12 queries) — fine for client.
  const queries: Promise<Row[]>[] = [];
  for (const provider of ['swiggy', 'zomato'] as AggregatorProvider[]) {
    for (const concept of CONCEPTS) {
      for (const slot of slotsFor(provider, concept)) {
        queries.push(
          (async () => {
            const { data, error } = await fooddb
              .from('flax_metrics_daily')
              .select('outlet_id, provider, category, metric, value')
              .eq('provider', provider)
              .eq('category', slot.category)
              .eq('metric', slot.metric)
              .gte('date', start)
              .lte('date', end);
            if (error) throw error;
            return (data ?? []) as unknown as Row[];
          })(),
        );
      }
    }
  }
  const parts = await Promise.all(queries);
  return parts.flat();
}

function emptySnapshot(): ProviderSnapshot {
  return {
    netSales: 0,
    deliveredOrders: 0,
    ordersPlaced: 0,
    impressions: 0,
    poorRated: 0,
    totalRated: 0,
    aov: 0,
    funnelConvPct: 0,
    poorRatedPct: 0,
  };
}

function rollup(rows: Row[]): Map<string, { swiggy: ProviderSnapshot; zomato: ProviderSnapshot }> {
  const byOutlet = new Map<string, { swiggy: ProviderSnapshot; zomato: ProviderSnapshot }>();

  // Build reverse lookup: (provider, category, metric) → concept
  const slotToConcept = new Map<string, MetricConcept>();
  for (const provider of ['swiggy', 'zomato'] as AggregatorProvider[]) {
    for (const concept of CONCEPTS) {
      for (const slot of slotsFor(provider, concept)) {
        slotToConcept.set(`${provider}::${slot.category}::${slot.metric}`, concept);
      }
    }
  }

  for (const r of rows) {
    const concept = slotToConcept.get(`${r.provider}::${r.category}::${r.metric}`);
    if (!concept) continue;
    const ourSlug = FOODDB_TO_OUTLET[r.outlet_id];
    if (!ourSlug) continue;
    const entry = byOutlet.get(ourSlug) ?? { swiggy: emptySnapshot(), zomato: emptySnapshot() };
    const snap = entry[r.provider];
    const v = Number(r.value ?? 0);
    switch (concept) {
      case 'sales.net_sales':
        snap.netSales += v;
        break;
      case 'sales.delivered_orders':
        snap.deliveredOrders += v;
        break;
      case 'funnel.orders_placed':
        snap.ordersPlaced += v;
        break;
      case 'funnel.impressions':
        snap.impressions += v;
        break;
      case 'ratings.poor_rated':
        snap.poorRated += v;
        break;
      case 'ratings.total_rated':
        snap.totalRated += v;
        break;
    }
    byOutlet.set(ourSlug, entry);
  }

  for (const entry of byOutlet.values()) {
    for (const snap of [entry.swiggy, entry.zomato]) {
      snap.aov = snap.deliveredOrders > 0 ? snap.netSales / snap.deliveredOrders : 0;
      snap.funnelConvPct = snap.impressions > 0 ? (snap.ordersPlaced / snap.impressions) * 100 : 0;
      snap.poorRatedPct = snap.totalRated > 0 ? (snap.poorRated / snap.totalRated) * 100 : 0;
    }
  }
  return byOutlet;
}

export function useOutletProviderBreakdown({ period }: Args) {
  const current = getPeriodRange(period === 'custom' ? '7d' : period);
  const previous = getCompareRange(current, 'previous');

  return useQuery<Result>({
    queryKey: ['outlet-provider-breakdown', current.start.toISOString()],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([fetchAll(current), fetchAll(previous)]);
      const cur = rollup(curRows);
      const prev = rollup(prevRows);

      const ids = new Set<string>([...cur.keys(), ...prev.keys()]);
      const outlets: OutletBreakdown[] = Array.from(ids)
        .map((outletId) => {
          const c = cur.get(outletId) ?? { swiggy: emptySnapshot(), zomato: emptySnapshot() };
          const p = prev.get(outletId) ?? { swiggy: emptySnapshot(), zomato: emptySnapshot() };
          const totalSales = c.swiggy.netSales + c.zomato.netSales;
          return {
            outletId,
            swiggy: c.swiggy,
            zomato: c.zomato,
            prevSwiggy: p.swiggy,
            prevZomato: p.zomato,
            swiggySharePct: totalSales > 0 ? (c.swiggy.netSales / totalSales) * 100 : 50,
          };
        })
        .sort((a, b) => b.swiggy.netSales + b.zomato.netSales - (a.swiggy.netSales + a.zomato.netSales));

      return { outlets, current, previous };
    },
    staleTime: 60_000,
  });
}
